import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { setViewingMode } from '../../../../../../viewer/view-scenes';
import { ViewingMode, ViewLayer, ViewSettings } from '../../../../../../viewer/view-settings';
import { CheckBox } from '../../../../check-box/check-box';
import { Icon } from '../../../../icon/icon';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Select } from '../../../../select/select';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { Stack } from '../../../../stack/stack';
import { PointCloudSettings } from '../point-cloud/point-cloud-settings';
import { LayerOpacity } from './layer-opacity';
import { OrthophotoSettings } from './orthophoto-settings';
import { TiledModelSettings } from './tiled-model-settings';
import { useViewSettings } from './use-view-settings';

const ViewSettingsPanel = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { skyview } = React.useContext(SkyviewContext);
  const { layers, camera, classicView } = useViewSettings();
  const [expandedLayer, setExpandedLayer] = React.useState<ViewLayer>();

  const isFlexible = ViewSettings.instance.isFlexible;
  const available: Record<ViewLayer, boolean> = {
    orthophoto: skyview.isOrthoSceneAvailable(),
    tiledModel: skyview.is3DSceneAvailable(),
    pointCloud: skyview.isPointCloudSceneAvailable(),
  };
  // A layer's settings can be changed while it's shown
  const isShown = (layer: ViewLayer) =>
    available[layer] && (isFlexible ? layers[layer] : classicView === layer);
  const shown: Record<ViewLayer, boolean> = {
    orthophoto: isShown('orthophoto'),
    tiledModel: isShown('tiledModel'),
    pointCloud: isShown('pointCloud'),
  };

  const names: Record<ViewLayer, string> = {
    orthophoto: t('viewSettings.layers.orthophoto', { ns: 'skyviewToolbar' }),
    tiledModel: t('viewSettings.layers.tiledModel', { ns: 'skyviewToolbar' }),
    pointCloud: t('viewSettings.layers.pointCloud', { ns: 'skyviewToolbar' }),
  };

  const settingsByLayer: Partial<Record<ViewLayer, React.ReactNode>> = {
    orthophoto: (
      <OrthophotoSettings altitudeDisabled={camera !== 'perspective'} showAltitude={isFlexible} />
    ),
    tiledModel: (
      <Stack direction="column" spacing={1}>
        <LayerOpacity layer="tiledModel" />
        <TiledModelSettings />
      </Stack>
    ),
    pointCloud: (
      <Stack direction="column" spacing={1}>
        <LayerOpacity layer="pointCloud" />
        <PointCloudSettings />
      </Stack>
    ),
  };

  const onViewingModeChanged = (mode: ViewingMode) => {
    setExpandedLayer(undefined);
    setViewingMode(mode);
  };

  const renderLayer = (layer: ViewLayer) => {
    const name = names[layer];
    const settings = settingsByLayer[layer];
    const expanded = expandedLayer === layer && shown[layer];

    return (
      <Stack direction="column" key={layer} spacing={0.5}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
          {isFlexible ? (
            <CheckBox
              checked={layers[layer] && available[layer]}
              disabled={!available[layer]}
              onChange={(e) => ViewSettings.instance.setLayerVisible(layer, e.target.checked)}
            >
              {name}
            </CheckBox>
          ) : (
            <LayerName $disabled={!available[layer]}>{name}</LayerName>
          )}
          {settings && (
            <Icon
              color={theme.color.gray.dark}
              disabled={!shown[layer]}
              icon={[expanded ? 'fas' : 'fal', 'gear']}
              title={t('viewSettings.layers.settings', { ns: 'skyviewToolbar' })}
              onClick={
                shown[layer] ? () => setExpandedLayer(expanded ? undefined : layer) : undefined
              }
            />
          )}
        </Stack>
        {expanded && <LayerSettings>{settings}</LayerSettings>}
      </Stack>
    );
  };

  return (
    <Stack direction="column" spacing={1.5}>
      {ViewSettings.instance.canChooseViewingMode && (
        <LabelledContainer
          noWrap={true}
          text={t('viewSettings.viewingMode.title', { ns: 'skyviewToolbar' })}
        >
          {(formElementId) => (
            <Select
              id={formElementId()}
              options={[
                {
                  id: 'classic',
                  name: t('viewSettings.viewingMode.classic', { ns: 'skyviewToolbar' }),
                },
                {
                  id: 'flexible',
                  name: t('viewSettings.viewingMode.flexible', { ns: 'skyviewToolbar' }),
                },
              ]}
              value={ViewSettings.instance.viewingMode}
              onChange={(e) => onViewingModeChanged(e.target.value as ViewingMode)}
            />
          )}
        </LabelledContainer>
      )}

      <Stack direction="column" spacing={1}>
        {(['orthophoto', 'tiledModel', 'pointCloud'] as const).map(renderLayer)}
      </Stack>
    </Stack>
  );
};

const LayerName = styled.span<{ $disabled: boolean }>`
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`;

const LayerSettings = styled.div`
  padding-left: 1em;
`;

export { ViewSettingsPanel };
